import { TriangleAlert } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "../../components/DashboardLayout/DashboardLayout";
import ExpandableText from "../../components/ExpandableText";
import { DashboardCardSkeleton } from "../../components/Skeleton";
import { unwrapPaginated } from "../../config/api";
import { formatDate } from "../../i18n/format";
import { formatScore, hasTrustBadge, trustBadgeLabel } from "../../lib/trustLabels";
import { useArticlesList } from "../../services/articles/generated/articles/articles";
import { useCompaniesMyCompaniesRetrieve } from "../../services/companies/generated/companies/companies";
import type { Company } from "../../services/companies/generated/model";
import {
  useMatchRequestsMyRequestsRetrieve,
  useMatchRequestsTotalMatchResultsRetrieve,
} from "../../services/match-requests/generated/match-requests/match-requests";
import type { MatchRequest } from "../../services/match-requests/generated/model";

const B2BDashboardPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation(["b2b", "common"]);

  // Fetch user's companies
  const {
    data: companiesData,
    isLoading: companiesLoading,
    // error: companiesError,
  } = useCompaniesMyCompaniesRetrieve();
  const companies = unwrapPaginated<Company>(companiesData);

  // Fetch articles (regulations and market intelligence)
  const {
    data: articlesData,
    isLoading: articlesLoading,
    // error: articlesError,
  } = useArticlesList({ category: "REGULATION" });

  // Fetch match requests (first page — used for the recent-activity table)
  const {
    data: matchRequestsData,
    isLoading: matchRequestsLoading,
    // error: matchRequestsError,
  } = useMatchRequestsMyRequestsRetrieve();

  // Request and match totals come from the server-side summary, so they are
  // counted separately and are not capped at the first page of requests.
  const { data: totalMatchesData, isLoading: totalMatchesLoading } = useMatchRequestsTotalMatchResultsRetrieve();

  // Extract data from paginated responses
  const articles = articlesData?.results || [];
  const matchRequests: MatchRequest[] = matchRequestsData?.results ?? [];

  const handleNavClick = (path: string) => {
    navigate(path);
  };

  // Calculate dashboard metrics from real data
  const companyCount = companies.length;
  const articleCount = articles.length;
  const matchRequestCount = totalMatchesData?.summary?.totalRequests ?? matchRequestsData?.count ?? 0;
  const matchCount = totalMatchesData?.summary?.totalResults ?? 0;
  const highPriorityArticles = articles.filter(
    (a) => a.title.toLowerCase().includes("urgent") || a.title.toLowerCase().includes("critical"),
  ).length;

  // Get latest article for alert bar
  const latestArticle = articles[0];

  // Format match requests for recent activity table
  // One row per match request: what was asked for, its status and how many
  // matches it produced. (The old "Partner" column showed our own company and
  // "Score" averaged the request's criterion weights — neither was real.)
  const recentActivity = matchRequests.slice(0, 5).map((request) => {
    const status = request.isDraft ? "draft" : request.status || "pending";
    return {
      id: request.id,
      date: formatDate(request.createdAt),
      type: t("dashboard.activity.matchRequest"),
      title: request.requirementTitle || request.productServiceName || t("dashboard.activity.untitledRequest"),
      status,
      statusTone:
        status === "completed" ? "success" : status === "working" ? "info" : status === "failed" ? "danger" : "warning",
      matches: request.results?.length ?? 0,
    };
  });

  return (
    <DashboardLayout>
      <header className="dashboard__section-header">
        <div>
          <h1 className="dashboard__title">{t("dashboard.title")}</h1>
          <p className="dashboard__subtitle">{t("dashboard.subtitle")}</p>
        </div>
      </header>

      {/* Loading State - Skeleton Cards */}
      {companiesLoading || articlesLoading || matchRequestsLoading || totalMatchesLoading ? (
        <div className="dashboard__cards-row">
          <DashboardCardSkeleton count={3} />
        </div>
      ) : (
        /* Dashboard Cards with Real Data */
        <div className="dashboard__cards-row">
          <article className="dashboard-card">
            <div className="dashboard-card__icon dashboard-card__icon--primary">
              <i className="fa-solid fa-bolt" />
            </div>
            <div className="dashboard-card__header">
              <h2 className="dashboard-card__title">{t("dashboard.cards.matches")}</h2>
              <span className="dashboard-card__chip dashboard-card__chip--soft-success">
                {t("dashboard.cards.requestCount", { count: matchRequestCount })}
              </span>
            </div>
            <p className="dashboard-card__value">{matchCount}</p>
            <p className="dashboard-card__meta">
              {t("dashboard.cards.fromMatchRequests", { count: matchRequestCount })}
            </p>
          </article>

          <article className="dashboard-card">
            <div className="dashboard-card__icon dashboard-card__icon--success">
              <i className="fa-regular fa-circle-check" />
            </div>
            <div className="dashboard-card__header">
              <h2 className="dashboard-card__title">{t("dashboard.cards.complianceStatus")}</h2>
              <span
                className={`dashboard-card__chip ${hasTrustBadge(companies[0]) ? "dashboard-card__chip--success" : ""}`}
              >
                {companies[0] ? trustBadgeLabel(companies[0]) : t("common:states.notAvailable")}
              </span>
            </div>
            <p className="dashboard-card__value">{formatScore(companies[0]?.overallComplianceScore)}</p>
            <p className="dashboard-card__meta">
              {companies[0]
                ? t("dashboard.cards.companyCompliance", { name: companies[0].companyName })
                : t("dashboard.cards.noCompanyData")}
            </p>
          </article>

          <article className="dashboard-card">
            <div className="dashboard-card__icon dashboard-card__icon--warning">
              <i className="fa-solid fa-triangle-exclamation" />
            </div>
            <div className="dashboard-card__header">
              <h2 className="dashboard-card__title">{t("dashboard.cards.marketAlerts")}</h2>
              <span className="dashboard-card__chip dashboard-card__chip--danger">
                {highPriorityArticles > 0
                  ? t("dashboard.cards.highCount", { count: highPriorityArticles })
                  : t("common:states.none")}
              </span>
            </div>
            <p className="dashboard-card__value">{articleCount}</p>
            <p className="dashboard-card__meta">
              {articleCount > 0 ? t("dashboard.cards.reviewUpdates") : t("dashboard.cards.noAlerts")}
            </p>
          </article>
        </div>
      )}

      {/* Latest Market Alert from Real Data */}
      {latestArticle && (
        <section className="dashboard__alert-bar" aria-label={t("dashboard.alert.ariaLabel")}>
          <div className="dashboard__alert-content">
            <div className="dashboard__alert-icon">
              <TriangleAlert size={24} />
            </div>
            <div className="dashboard__alert-content-container">
              <p className="dashboard__alert-title">{t("dashboard.alert.title")}</p>
              <ExpandableText
                className="dashboard__alert-text"
                text={latestArticle.brief ? `${latestArticle.title} · ${latestArticle.brief}` : latestArticle.title}
                maxLines={2}
              />
            </div>
          </div>
          <button
            type="button"
            className="dashboard__alert-link"
            onClick={() => window.open(latestArticle.fullArticleLink, "_blank")}
          >
            {t("common:actions.viewDetails")}
          </button>
        </section>
      )}

      {/* Stats Panel with Real Company Data */}
      <section className="dashboard__stats-panel" aria-label={t("dashboard.stats.ariaLabel")}>
        <div className="dashboard__stats-gradient">
          <div className="dashboard__stats-item">
            <p className="dashboard__stats-label">{t("dashboard.stats.yourCompanies")}</p>
            <p className="dashboard__stats-value">{companyCount}</p>
          </div>
          <div className="dashboard__stats-item">
            <p className="dashboard__stats-label">{t("dashboard.stats.complianceScore")}</p>
            <p className="dashboard__stats-value">{formatScore(companies[0]?.overallComplianceScore)}</p>
          </div>
          <div className="dashboard__stats-item">
            <p className="dashboard__stats-label">{t("dashboard.stats.marketUpdates")}</p>
            <p className="dashboard__stats-value">{articleCount}</p>
          </div>
        </div>
      </section>

      <section className="dashboard__table-section" aria-label={t("dashboard.activity.ariaLabel")}>
        <header className="dashboard__table-header">
          <h2 className="dashboard__table-title">{t("dashboard.activity.title")}</h2>
          <button type="button" className="dashboard__table-link">
            {t("dashboard.activity.viewAll")}
          </button>
        </header>

        <div className="dashboard__table">
          {recentActivity.length > 0 ? (
            <>
              <div className="dashboard__table-row dashboard__table-row--head">
                <span>{t("common:labels.date")}</span>
                <span>{t("dashboard.activity.columns.type")}</span>
                <span>{t("dashboard.activity.columns.request")}</span>
                <span>{t("common:labels.status")}</span>
                <span>{t("dashboard.activity.columns.matches")}</span>
              </div>

              {recentActivity.map((row) => (
                <div key={row.id} className="dashboard__table-row">
                  <span className="dashboard__table-cell dashboard__table-cell--date">
                    <i className="fa-regular fa-calendar" />
                    {row.date}
                  </span>
                  <span>{row.type}</span>
                  <span className="dashboard__table-partner">
                    <span className="dashboard__table-partner-avatar">{row.title.charAt(0)}</span>
                    {row.title}
                  </span>
                  <span>
                    <span className={`dashboard__status-pill dashboard__status-pill--${row.statusTone}`}>
                      {row.status}
                    </span>
                  </span>
                  <span>{row.matches}</span>
                </div>
              ))}
            </>
          ) : (
            <div className="dashboard__table-empty">
              <div className="dashboard__empty-icon">
                <i className="fa-solid fa-chart-line" />
              </div>
              <h3 className="dashboard__empty-title">{t("dashboard.empty.title")}</h3>
              <p className="dashboard__empty-description">{t("dashboard.empty.description")}</p>
              <button type="button" className="dashboard__empty-action" onClick={() => handleNavClick("/b2b/requests")}>
                <i className="fa-solid fa-plus" />
                {t("dashboard.empty.cta")}
              </button>
              <div className="dashboard__empty-stats">
                <div className="dashboard__empty-stat">
                  <i className="fa-solid fa-bolt" />
                  <span>{t("dashboard.empty.fastMatching")}</span>
                </div>
                <div className="dashboard__empty-stat">
                  <i className="fa-solid fa-shield-check" />
                  <span>{t("dashboard.empty.verifiedPartners")}</span>
                </div>
                <div className="dashboard__empty-stat">
                  <i className="fa-solid fa-chart-simple" />
                  <span>{t("dashboard.empty.smartAnalytics")}</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>
    </DashboardLayout>
  );
};

export default B2BDashboardPage;
